<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useIncidentStore } from '@/stores/incident'
import { useAuthStore } from '@/stores/auth'
import { useWizardStore } from '@/stores/wizard'
import { useIncidentSummaryStore } from '@/stores/incidentSummary'
import { IncidentStatus } from '@/types'
import type { Incident } from '@/types'
import type { Timestamp } from 'firebase/firestore'
import FosButton from '@/components/ui/FosButton.vue'
import UserAvatar from '@/components/common/UserAvatar.vue'
import ThemeToggle from '@/components/layout/header/ThemeToggle.vue'
import WizardEntryModal from '@/components/wizard/WizardEntryModal.vue'
import QuickCreateModal from '@/components/wizard/QuickCreateModal.vue'

const router = useRouter()
const incidentStore = useIncidentStore()
const authStore = useAuthStore()
const wizardStore = useWizardStore()
const summaryStore = useIncidentSummaryStore()

const showEntryModal = ref(false)
const showQuickCreate = ref(false)
const archivedExpanded = ref(false)
const draftsExpanded = ref(false)

onMounted(() => {
  const agencyId = authStore.userProfile?.agency_id
  if (agencyId) incidentStore.subscribeToIncidents(agencyId)
})

onUnmounted(() => {
  incidentStore.unsubscribeAll()
  summaryStore.teardown()
})

watch(
  () => incidentStore.incidents.map((i) => i.incident_id),
  (ids) => summaryStore.subscribeToAll(ids),
  { immediate: true },
)

function sortDesc(list: Incident[]): Incident[] {
  return [...list].sort((a, b) => {
    const aMs = a.created_at?.toMillis() ?? 0
    const bMs = b.created_at?.toMillis() ?? 0
    return bMs - aMs
  })
}

const activeIncidents = computed(() =>
  sortDesc(incidentStore.incidents.filter((i) => i.status === IncidentStatus.Active)),
)

const draftIncidents = computed(() =>
  sortDesc(incidentStore.incidents.filter((i) => i.status === IncidentStatus.Draft)),
)

const archivedIncidents = computed(() =>
  sortDesc(
    incidentStore.incidents.filter(
      (i) => i.status !== IncidentStatus.Active && i.status !== IncidentStatus.Draft,
    ),
  ),
)

function statusBadgeClass(status: IncidentStatus): string {
  if (status === IncidentStatus.Active) return 'bg-green-900/40 text-green-400'
  if (status === IncidentStatus.Suspended) return 'bg-amber-900/40 text-amber-400'
  if (status === IncidentStatus.Draft) return 'bg-purple-100 text-purple-700'
  return 'bg-surface-container-highest text-outline'
}

function statusLabel(status: IncidentStatus): string {
  if (status === IncidentStatus.Active) return 'Active'
  if (status === IncidentStatus.Suspended) return 'Suspended'
  if (status === IncidentStatus.Draft) return 'Draft'
  return 'Closed'
}

function timeElapsed(createdAt: Timestamp | undefined): string {
  if (!createdAt) return ''
  const ms = Date.now() - createdAt.toMillis()
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs} hr${hrs !== 1 ? 's' : ''} ago`
  const days = Math.floor(hrs / 24)
  return `${days} day${days !== 1 ? 's' : ''} ago`
}

function openIncident(incident: Incident) {
  router.push({ name: 'incident-overview', params: { id: incident.incident_id } })
}

function onWizardStart() {
  wizardStore.init()
  router.push({ name: 'incident-wizard' })
}

function onQuickCreate() {
  showQuickCreate.value = true
}

async function onManual() {
  const agencyId = authStore.userProfile?.agency_id ?? ''
  const userId = authStore.currentUser?.uid ?? ''
  const { incidentId } = await incidentStore.createIncident({
    incident_name: 'New Incident',
    incident_number: null,
    contact_phone: null,
    agency_id: agencyId,
    created_by: userId,
  })
  localStorage.setItem(`setup-banner-${incidentId}`, 'show')
  router.push({ name: 'incident-overview', params: { id: incidentId } })
}

function formatOpDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number)
  const d = new Date(year!, month! - 1, day!)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function continueDraft(incident: Incident) {
  wizardStore.init({
    mission_name: incident.incident_name,
    mission_type: incident.mission_type ?? undefined,
    start_date: incident.start_date ?? undefined,
    start_time: incident.start_time ?? undefined,
  })
  router.push({ name: 'incident-wizard' })
}
</script>

<template>
  <div class="h-full overflow-y-auto">
    <header class="h-12 flex items-center justify-between pr-4 bg-background border-b border-border">
      <div class="flex items-center h-full overflow-hidden">
        <div class="w-20 flex items-center justify-center gap-2 shrink-0">
          <div class="w-[24px] shrink-0" />
          <img src="/assets/logo.svg" alt="First on Scene" class="h-8 w-auto shrink-0" />
        </div>
        <div class="h-4 w-px bg-border shrink-0" />
        <div class="flex items-center gap-2.5 pl-2.5 overflow-hidden">
          <span class="font-headline font-bold text-sm text-on-surface uppercase tracking-tight truncate">
            First On Scene
          </span>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <ThemeToggle />
        <UserAvatar />
      </div>
    </header>

    <div class="max-w-3xl mx-auto px-4 py-8">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-xl font-bold text-on-surface uppercase tracking-wide">Incidents</h1>
        <FosButton variant="primary" @click="showEntryModal = true">
          <span class="material-symbols-outlined text-[16px]">add</span>
          New Incident
        </FosButton>
      </div>

      <!-- Empty state -->
      <div
        v-if="activeIncidents.length === 0 && archivedIncidents.length === 0"
        class="text-center py-20 text-outline"
      >
        No incidents yet. Start a new one.
      </div>

      <!-- Active incident cards -->
      <div class="flex flex-col gap-2">
        <div
          v-for="incident in activeIncidents"
          :key="incident.incident_id"
          class="bg-surface-container px-5 py-4 cursor-pointer hover:bg-surface-container-high transition-colors"
          @click="openIncident(incident)"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-on-surface truncate">
                {{ incident.incident_name }}
              </p>
              <p class="text-sm text-on-surface-variant mt-0.5 font-mono">
                {{ incident.incident_number ?? 'No number assigned' }}
              </p>
            </div>
            <div class="flex flex-col items-end gap-1 shrink-0">
              <span
                class="font-mono text-xs uppercase tracking-widest px-2 py-0.5"
                :class="statusBadgeClass(incident.status)"
              >
                {{ statusLabel(incident.status) }}
              </span>
              <span class="text-xs text-outline font-mono">
                {{ timeElapsed(incident.created_at) }}
              </span>
            </div>
          </div>
          <div class="flex items-center justify-between mt-2 pt-2 border-t border-border">
            <span class="text-xs font-mono text-outline">
              <template v-if="summaryStore.summaries.get(incident.incident_id)?.latestOp">
                OP {{ summaryStore.summaries.get(incident.incident_id)!.latestOp!.op_number }}
                · {{ formatOpDate(summaryStore.summaries.get(incident.incident_id)!.latestOp!.date) }}
              </template>
              <template v-else>No OPs</template>
            </span>
            <span
              v-if="summaryStore.summaries.get(incident.incident_id)?.isCheckedIn"
              class="flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-primary"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-primary inline-block" />
              Checked In
            </span>
          </div>
        </div>
      </div>

      <!-- Draft incidents -->
      <div v-if="draftIncidents.length > 0" class="mt-6">
        <button
          class="flex items-center gap-2 text-xs font-label uppercase tracking-widest text-outline hover:text-on-surface-variant transition-colors mb-3"
          @click="draftsExpanded = !draftsExpanded"
        >
          <svg
            class="w-4 h-4 transition-transform"
            :class="draftsExpanded ? 'rotate-90' : ''"
            fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          Drafts ({{ draftIncidents.length }})
        </button>

        <div v-if="draftsExpanded" class="flex flex-col gap-2">
          <div
            v-for="incident in draftIncidents"
            :key="incident.incident_id"
            class="bg-surface-container border border-border px-5 py-4"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1 min-w-0">
                <p class="font-semibold text-on-surface truncate">
                  {{ incident.incident_name }}
                </p>
                <p class="text-sm text-on-surface-variant mt-0.5 font-mono">
                  {{ incident.incident_number ?? 'No number assigned' }}
                </p>
              </div>
              <div class="flex flex-col items-end gap-1 shrink-0">
                <span
                  class="font-mono text-xs uppercase tracking-widest px-2 py-0.5"
                  :class="statusBadgeClass(incident.status)"
                >
                  Draft
                </span>
                <span class="text-xs text-outline font-mono">
                  {{ timeElapsed(incident.created_at) }}
                </span>
              </div>
            </div>
            <div
              v-if="summaryStore.summaries.get(incident.incident_id)?.latestOp"
              class="flex items-center justify-between mt-2 pt-2 border-t border-border"
            >
              <span class="text-xs font-mono text-outline">
                OP {{ summaryStore.summaries.get(incident.incident_id)!.latestOp!.op_number }}
                · {{ formatOpDate(summaryStore.summaries.get(incident.incident_id)!.latestOp!.date) }}
              </span>
            </div>
            <div class="mt-3 flex gap-2">
              <FosButton variant="secondary" size="sm" @click="continueDraft(incident)">Continue setup</FosButton>
              <FosButton variant="secondary" size="sm" @click="openIncident(incident)">Open</FosButton>
            </div>
          </div>
        </div>
      </div>

      <!-- Archived incidents (suspended + closed) -->
      <div v-if="archivedIncidents.length > 0" class="mt-6">
        <button
          class="flex items-center gap-2 text-xs font-label uppercase tracking-widest text-outline hover:text-on-surface-variant transition-colors mb-3"
          @click="archivedExpanded = !archivedExpanded"
        >
          <svg
            class="w-3 h-3 transition-transform"
            :class="archivedExpanded ? 'rotate-90' : ''"
            fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          Archived ({{ archivedIncidents.length }})
        </button>

        <div v-if="archivedExpanded" class="flex flex-col gap-2">
          <div
            v-for="incident in archivedIncidents"
            :key="incident.incident_id"
            class="bg-surface-container px-5 py-4 cursor-pointer hover:bg-surface-container-high transition-colors opacity-60"
            @click="openIncident(incident)"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1 min-w-0">
                <p class="font-semibold text-on-surface truncate">
                  {{ incident.incident_name }}
                </p>
                <p class="text-sm text-on-surface-variant mt-0.5 font-mono">
                  {{ incident.incident_number ?? 'No number assigned' }}
                </p>
              </div>
              <div class="flex flex-col items-end gap-1 shrink-0">
                <span
                  class="font-mono text-xs uppercase tracking-widest px-2 py-0.5"
                  :class="statusBadgeClass(incident.status)"
                >
                  {{ statusLabel(incident.status) }}
                </span>
                <span class="text-xs text-outline font-mono">
                  {{ timeElapsed(incident.created_at) }}
                </span>
              </div>
            </div>
            <div
              v-if="summaryStore.summaries.get(incident.incident_id)?.latestOp"
              class="flex items-center mt-2 pt-2 border-t border-border"
            >
              <span class="text-xs font-mono text-outline">
                OP {{ summaryStore.summaries.get(incident.incident_id)!.latestOp!.op_number }}
                · {{ formatOpDate(summaryStore.summaries.get(incident.incident_id)!.latestOp!.date) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <WizardEntryModal
      v-model:visible="showEntryModal"
      @wizard-start="onWizardStart"
      @quick-create="onQuickCreate"
      @manual="onManual"
    />
    <QuickCreateModal
      v-model:visible="showQuickCreate"
      @switch-to-wizard="router.push({ name: 'incident-wizard' })"
    />
  </div>
</template>
